// apps/pdf-suite/web/src/lib/clientContext.js
// One source of the diagnostic context every Scrivar PDF support submission
// carries: which build the customer is on, and what they are running it on.
//
// Nothing here is identity. Tier and licence are derived SERVER-side from the
// account that matches the sender's email — a PDF client never sends licence
// material. The server clamps both strings to 64 characters and strips
// newlines, so these stay short and human-readable rather than exhaustive.
//
// Every branch degrades to null instead of throwing: a support ticket must
// always be submittable, including from a browser that exposes none of this.

const OS_NAME = { darwin: 'macOS', win32: 'Windows', linux: 'Linux' };

// Browser detection order matters: Edge and Opera both also claim "Chrome",
// and Chrome claims "Safari", so the specific patterns must come first.
const UA_BROWSERS = [
  // Edge is Chromium-based, so its UA also carries Chrome's token (and, on
  // iOS, Safari's) -- every Edge variant must be checked before Chrome.
  // Desktop, iOS, and Android each stamp a differently-named brand token.
  [/\bEdgA\/(\d+)/, 'Edge'],
  [/\bEdgiOS\/(\d+)/, 'Edge'],
  [/\bEdg\/(\d+)/, 'Edge'],
  [/\bOPR\/(\d+)/, 'Opera'],
  // Apple requires every iOS browser to run on WebKit, so Chrome/Firefox on
  // iOS ship no "Chrome/"/"Firefox/" token at all -- only their own
  // CriOS/FxiOS brand token -- and must be matched before the Safari
  // fallback below, which would otherwise misclaim them.
  [/\bFxiOS\/(\d+)/, 'Firefox'],
  [/\bFirefox\/(\d+)/, 'Firefox'],
  [/\bCriOS\/(\d+)/, 'Chrome'],
  [/\bChrome\/(\d+)/, 'Chrome'],
  // Real Safari is checked last: every browser above also carries a
  // "Safari/" token (the shared engine badge). iOS Safari additionally
  // inserts a "Mobile/15E148" token between "Version/N" and "Safari/", so
  // this can't require the two to sit directly next to each other.
  [/\bVersion\/(\d+)[\d.]*.*Safari\//, 'Safari'],
];

const UA_OS = [
  [/\bWindows NT\b/, 'Windows'],
  [/\b(iPhone|iPad)\b/, 'iOS'],
  [/\bMac OS X\b/, 'macOS'],
  [/\bAndroid\b/, 'Android'],
  [/\bLinux\b/, 'Linux'],
];

// Mirrors the Scrivar Office launcher's string exactly —
// "darwin arm64 · macOS 23.5.0" — so both products read the same in one inbox.
// `arch` (a preload-exposed sync property) and `osRelease` (fetched from the
// main process — see collectClientContext) only exist in desktop builds from
// the next release onward — v1.0.36 or later (v1.0.35 was already taken by
// main's Arial font release). Pure: takes the release as a plain argument
// rather than reading it off the bridge itself, so it has no async/IPC
// concerns of its own.
export function desktopPlatform(api, osRelease) {
  const base = [api.platform, api.arch].filter(Boolean).join(' ');
  const osName = OS_NAME[api.platform];
  if (osName && osRelease) return `${base} · ${osName} ${osRelease}`;
  return base || null;
}

export function browserPlatform(nav) {
  const uad = nav.userAgentData;
  if (uad && Array.isArray(uad.brands)) {
    // Chromium pads `brands` with a deliberately-garbled "Not A Brand" entry
    // whose punctuation varies between releases, hence the loose pattern.
    const real = uad.brands.filter((b) => b && b.brand && !/not[^a-z]*a[^a-z]*brand/i.test(b.brand));
    const brand = real.find((b) => !/^chromium$/i.test(b.brand)) || real[0];
    const parts = [brand ? `${brand.brand} ${brand.version}` : null, uad.platform || null].filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }

  const ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
  if (!ua) return null;
  let browser = null;
  for (const [re, name] of UA_BROWSERS) {
    const m = re.exec(ua);
    if (m) { browser = `${name} ${m[1]}`; break; }
  }
  let osName = null;
  for (const [re, name] of UA_OS) {
    if (re.test(ua)) { osName = name; break; }
  }
  return [browser, osName].filter(Boolean).join(' · ') || null;
}

// Calls a bridge getter and degrades ANY failure to null — a rejected
// promise (the common case: the IPC round-trip failed) or a synchronous
// throw from the call itself. `Promise.resolve(fn()).catch(...)` only
// guards the former: if `fn()` throws before it ever returns a promise, that
// throw happens synchronously inside this function's own call frame, so it
// still needs a try/catch around the call, not just a `.catch` on the
// result. Every caller of collectClientContext awaits it inside its submit
// try, so an uncaught synchronous throw here would fail the whole ticket
// submission instead of just dropping one fact off the footer.
async function safeInvoke(fn) {
  try {
    return await Promise.resolve(fn());
  } catch {
    return null;
  }
}

export async function collectClientContext() {
  if (typeof window === 'undefined') return { appVersion: null, platform: null };

  const api = window.electronAPI;
  if (api && api.isDesktop) {
    const version = api.getVersion ? await safeInvoke(() => api.getVersion()) : null;
    const osRelease = api.getOsRelease ? await safeInvoke(() => api.getOsRelease()) : null;
    return {
      appVersion: typeof version === 'string' && version ? version : null,
      platform: desktopPlatform(api, typeof osRelease === 'string' && osRelease ? osRelease : null),
    };
  }

  return {
    appVersion: null,
    platform: typeof navigator !== 'undefined' ? browserPlatform(navigator) : null,
  };
}
